export const BASE_URL_API =
  process.env.WP_API_URL?.slice(-1) === "/"
    ? `${process.env.WP_API_URL.slice(0, -1)}`
    : process.env.WP_API_URL;

export const BASE_URL_V1 = `${BASE_URL_API}/wp-json`;
export const BASE_URL_V3 = `${BASE_URL_API}/wp-json/acf/v3`;
export const BASE_URL = `${BASE_URL_API}/wp-json/wp/v2`;

export const getWpJsonUrl = (lang?: string, defaultLang?: string) => {
  return lang && defaultLang && lang !== defaultLang
    ? `${BASE_URL_API}/${lang}/wp-json`
    : BASE_URL_V1;
};

export const getAundsV1Url = (lang?: string, defaultLang?: string) => {
  return `${getWpJsonUrl(lang, defaultLang)}/aunds/v1`;
};

export const getUrl = (lang?: string, defaultLang?: string, v1?: boolean) => {
  if (v1) {
    return getWpJsonUrl(lang, defaultLang);
  } else {
    return lang && defaultLang && lang !== defaultLang
      ? `${BASE_URL_API}/${lang}/wp-json/wp/v2`
      : BASE_URL;
  }
};

/**
 * Get relative path from link. Please pass also the type property for menu items to enable external links.
 * @param url
 * @param urlType (for menu links only)
 */
export const getRelativePathFromLink = (url: string, urlType = "default") => {
  if (validURL(url) && urlType !== "custom") {
    const u = new URL(url);
    return `${u.pathname}${u.search}${u.hash}`;
  }
  return url;
};

interface getPathNameArrayParams {
  url: string;
  blacklist?: string[];
}
export const getPathNameArray = ({
  url,
  blacklist = [],
}: getPathNameArrayParams) => {
  const path = getRelativePathFromLink(url);
  return path.split("/").filter((el) => el !== "" && !blacklist.includes(el));
};

const defaultLocale = process.env.DEFAULT_LOCALE;
const trailingSlash = process.env.TRAILING_SLASH === "true";

const splitUrlParts = (url: string) => {
  const hashIndex = url.indexOf("#");
  const urlWithoutHash = hashIndex === -1 ? url : url.slice(0, hashIndex);
  const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
  const searchIndex = urlWithoutHash.indexOf("?");

  return {
    pathname:
      searchIndex === -1
        ? urlWithoutHash
        : urlWithoutHash.slice(0, searchIndex),
    search: searchIndex === -1 ? "" : urlWithoutHash.slice(searchIndex),
    hash,
  };
};

const applyTrailingSlashToPathname = (pathname: string) => {
  const cleaned = pathname.replace(/\/+$/, "");

  return trailingSlash
    ? cleaned === ""
      ? "/"
      : `${cleaned}/`
    : cleaned || "/";
};

export const optimizeInternalUrl = (href: string) => {
  const { search, hash, ...urlParts } = splitUrlParts(href);
  let { pathname } = urlParts;

  pathname =
    pathname.replace(new RegExp(`^/${defaultLocale}(?=/|$)`), "") || "/";
  if (!pathname.startsWith("/")) pathname = `/${pathname}`;

  return `${applyTrailingSlashToPathname(pathname)}${search}${hash}`;
};

function validURL(str: string) {
  try {
    new URL(str);
    // eslint-disable-next-line  unused-imports/no-unused-vars
  } catch (e) {
    return false;
  }
  return true;
}

//TODO: maybe check this only once at startup
const checkPublicURLEnv = () => {
  if (!process.env.NEXT_PUBLIC_PUBLIC_URL) {
    throw new Error(
      "NEXT_PUBLIC_PUBLIC_URL not set but indispensable for isInternalLinkAbsolute",
    );
  }
  if (!validURL(process.env.NEXT_PUBLIC_PUBLIC_URL)) {
    throw new Error(
      "NEXT_PUBLIC_PUBLIC_URL " +
        process.env.NEXT_PUBLIC_PUBLIC_URL +
        " is no valid url",
    );
  }
};

//checks whether an link is internal and absolute
export const isInternalLinkAbsolute = (url: string) => {
  checkPublicURLEnv();
  return !!(
    validURL(url) &&
    (new URL(url).hostname ===
      new URL(process.env.NEXT_PUBLIC_PUBLIC_URL!).hostname ||
      (process.env.NEXT_PUBLIC_INTERNAL_ORIGINS &&
        process.env.NEXT_PUBLIC_INTERNAL_ORIGINS?.split(",")?.some?.(
          (internalHost) => {
            return (
              validURL(internalHost) &&
              new URL(url).hostname === new URL(internalHost).hostname
            );
          },
        )))
  );
};

/**
 * is intended to optimize all kinds of urls
 * currently does following:
 * - internal absolute urls are converted to relative ones
 * - trailing slash is applied or removed according to next.config.js to prevent 3xx errors
 *
 * @param url url to optimize
 * @returns optimized url
 */
export const optimizeURL = (url: string) => {
  if (!url) return;
  if (url === "/") return url;
  if (url.startsWith("/#")) {
    return url;
  }
  if (url.startsWith("#")) {
    return "/" + url;
  }
  if (isInternalLinkAbsolute(url)) {
    const relativeURL = getRelativePathFromLink(url);
    return optimizeInternalUrl(relativeURL);
  }
  if (!validURL(url)) {
    //assume relative url
    return optimizeInternalUrl(url);
  }
  return url;
};

export const setTrailingSlash = (url: string) => {
  const { pathname, search, hash } = splitUrlParts(url);

  return `${applyTrailingSlashToPathname(pathname)}${search}${hash}`;
};

/**
 * Builds an internal absolute link from a relative or absolute (independent which origin) url
 * @param url url (relative or absolute) to be transformed to an absolute internal link
 */
export const buildAbsoluteInternalLink = (url: string) => {
  checkPublicURLEnv();
  const relativeURL = getRelativePathFromLink(url);
  if (!relativeURL.startsWith("/")) {
    throw new Error(
      "can't build absolute internal link from relative url which does not start with /",
    );
  }
  return setTrailingSlash(
    `${process.env.NEXT_PUBLIC_PUBLIC_URL}${relativeURL}`,
  );
};

// Check if frontend slug is valid before passing it to the backend to prevent DOS attacks
export function isValidWpSlug(slug: string): boolean {
  // Slug must be a non-empty string, and only contain lowercase letters, numbers, and hyphens
  const wpSlugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

  // Trim whitespace from the slug (although WP slugs shouldn't have leading/trailing spaces)
  const trimmedSlug = slug?.trim();

  // Return true if the slug matches the pattern
  return wpSlugPattern.test(trimmedSlug);
}
